import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Package,
  Truck,
  MessageSquare,
  Gift,
  ShoppingBag,
  Plus,
  ChevronRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  Bell,
  Sparkles,
  ExternalLink
} from 'lucide-react';

export default function ClientHomeDashboard({ 
  orders = [], 
  requests = [], 
  recentActivity = [],
  onNavigate 
}) {
  const navigate = useNavigate();

  const ordersInDelivery = orders.filter(o => o.status === 'shipped').length;
  const pendingRequests = requests.filter(r => r.status === 'open').length;
  const latestOrder = orders[0];
  const latestRequest = requests[0];

  const getOrderStatusInfo = (status) => {
    switch (status) {
      case 'pending': return { label: 'In attesa', color: 'bg-yellow-100 text-yellow-700' };
      case 'confirmed': return { label: 'Confermato', color: 'bg-blue-100 text-blue-700' };
      case 'in_progress': return { label: 'In corso', color: 'bg-indigo-100 text-indigo-700' };
      case 'processing': return { label: 'In lavorazione', color: 'bg-purple-100 text-purple-700' };
      case 'ready_to_ship': return { label: 'Pronto per spedizione', color: 'bg-teal-100 text-teal-700' };
      case 'shipped': return { label: 'In spedizione', color: 'bg-cyan-100 text-cyan-700' };
      case 'delivered': return { label: 'Consegnato', color: 'bg-green-100 text-green-700' };
      case 'completed': return { label: 'Completato', color: 'bg-emerald-100 text-emerald-700' };
      case 'cancelled': return { label: 'Annullato', color: 'bg-red-100 text-red-700' };
      case 'refunded': return { label: 'Rimborsato', color: 'bg-orange-100 text-orange-700' };
      case 'disputed': return { label: 'In contestazione', color: 'bg-rose-100 text-rose-700' };
      default: return { label: status, color: 'bg-slate-100 text-slate-700' };
    }
  };

  return (
    <div className="space-y-6" data-testid="client-home-dashboard">
      {(ordersInDelivery > 0 || pendingRequests > 0) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#008080]" />
            In Evidenza
          </h2>
          <div className="flex flex-wrap gap-3">
            {ordersInDelivery > 0 && (
              <button onClick={() => onNavigate('active-orders')} 
                className="flex items-center gap-2 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-xl hover:bg-yellow-100 transition-colors">
                <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                <span className="font-medium text-yellow-800">{ordersInDelivery} ordine in consegna</span>
                <ChevronRight className="w-4 h-4 text-yellow-600" />
              </button>
            )}
            {pendingRequests > 0 && (
              <button onClick={() => onNavigate('open-requests')}
                className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                <span className="font-medium text-blue-800">{pendingRequests} richieste in attesa</span>
                <ChevronRight className="w-4 h-4 text-blue-600" />
              </button>
            )}
          </div>
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#008080]" />I Tuoi Ordini
            </h2>
            <Button variant="ghost" size="sm" onClick={() => onNavigate('active-orders')} className="text-[#008080]">
              Vedi tutti<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          {latestOrder ? (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="font-semibold text-slate-800">
                {latestOrder.items?.[0]?.title || 'Ordine'}
                {latestOrder.items?.length > 1 && ` +${latestOrder.items.length - 1}`}
              </p>
              <div className="mt-3 flex items-center justify-between">
                <Badge className={getOrderStatusInfo(latestOrder.status).color}>{getOrderStatusInfo(latestOrder.status).label}</Badge>
                <Button size="sm" variant="outline" onClick={() => onNavigate('active-orders')}>Vedi dettagli</Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-xl">
              <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessun ordine ancora</p>
              <Button className="mt-3 bg-[#008080]" onClick={() => navigate('/products')}>Esplora prodotti</Button>
            </div>
          )}
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#DAA520]" />Le Tue Richieste
            </h2>
            <Button variant="ghost" size="sm" onClick={() => onNavigate('open-requests')} className="text-[#008080]">
              Vedi tutte<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          {latestRequest ? (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="font-semibold text-slate-800">"{latestRequest.title}"</p>
              <div className="mt-3 flex items-center justify-between">
                <Badge variant="outline" className="border-[#008080] text-[#008080]">{latestRequest.proposals?.[0]?.count || 0} proposte</Badge>
                {(latestRequest.proposals?.[0]?.count || 0) > 0 && (
                  <Button size="sm" className="bg-[#008080]" onClick={() => onNavigate('open-requests')}>Vedi proposte</Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-xl">
              <Gift className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessuna richiesta ancora</p>
              <Button className="mt-3 bg-[#DAA520]" onClick={() => navigate('/requests')}>Crea una richiesta</Button>
            </div>
          )}
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#DAA520]" />Azioni Veloci
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button variant="outline" className="h-auto py-4 justify-start border-2 border-dashed border-[#DAA520] hover:bg-[#DAA520]/5 group" 
            onClick={() => navigate('/requests')}>
            <Plus className="w-5 h-5 mr-3 text-[#DAA520]" />
            <span className="text-left">
              <span className="block font-semibold text-slate-800">Nuova richiesta personalizzata</span>
              <span className="block text-xs text-slate-500">Fai creare qualcosa di unico</span>
            </span>
          </Button>
          <Button variant="outline" className="h-auto py-4 justify-start border-2 border-slate-200 hover:border-[#008080] hover:bg-[#008080]/5 group"
            onClick={() => navigate('/products')}>
            <Package className="w-5 h-5 mr-3 text-[#008080]" />
            <span className="text-left">
              <span className="block font-semibold text-slate-800">Esplora prodotti</span>
              <span className="block text-xs text-slate-500">Scopri le creazioni artigianali</span>
            </span>
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
